import React, { useCallback, useRef, useState, useEffect } from 'react';
import FormInput from './FormInput';
import SelectInput from './SelectInput';
import CraInternalContactSelect from './CraInternalContactSelect';

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Invalid date format:', dateValue);
    return '';
  }
};

const Filters = ({
  onSelectFilters,
  selectedFilters,
  onClearFilters,
  showFilters = {},
  alexAccessTypes = [],
  hubspotAccessTypes = [],
  cohorts = [],
  handleSelectAllUsers,
}) => {
  const emails = selectedFilters?.emails || '';
  const emailFileRef = useRef();
  const [filter, setFilter] = useState(selectedFilters || {});
  const [bulkEmailCount, setBulkEmailCount] = useState(0);
  const [bulkEmailList, setBulkEmailList] = useState([]);
  const bulkEmailLimit = parseInt(window.ENV?.BULK_EMAIL_LENGTH || process.env.REACT_APP_BULK_EMAIL_LENGTH || '1', 10);

  useEffect(() => {
    if (selectedFilters?.emails) {
      const emails = selectedFilters.emails
        .split(/\r\n|\n|,/)
        .map((item) => item.trim().replace(/"/g, '').toLowerCase())
        .filter((item) => item.match(/.+@.+\..+/) && item.length > 0);

      if (emails.length > bulkEmailLimit) {
        setBulkEmailCount(emails.length);
        setBulkEmailList(emails);
      } else {
        setBulkEmailCount(0);
        setBulkEmailList([]);
      }
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }
    }
  }, [selectedFilters?.emails, bulkEmailLimit]);

  const handleEmailFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        const csvData = loadEvent.target.result;
        const emails = csvData
          .split(/\r\n|\n|,/)
          .map((item) => item.trim().replace(/"/g, '').toLowerCase())
          .filter((item) => item.match(/.+@.+\..+/) && item.length > 0);

        if (emails.length > bulkEmailLimit) {
          setBulkEmailCount(emails.length);
          setBulkEmailList(emails);

          const newFilter = {
            ...filter,
            emails: emails.join('\n'),
          };
          setFilter(newFilter);
          onSelectFilters(newFilter);
        } else {
          setBulkEmailCount(0);
          setBulkEmailList([]);

          const newFilter = {
            ...filter,
            emails: emails.join('\n'),
          };
          setFilter(newFilter);
          onSelectFilters(newFilter);
        }
        if (handleSelectAllUsers) {
          handleSelectAllUsers(false);
        }

        event.target.value = '';
      };
      reader.readAsText(file);
    },
    [filter, onSelectFilters]
  );

  const handleEmailChange = useCallback(
    (value) => {
      if (Array.isArray(emails) && emails.length > bulkEmailLimit) {
        setBulkEmailCount(emails.length);
        setBulkEmailList(emails);

        const newFilter = {
          ...filter,
          emails: emails.join('\n'),
        };
        setFilter(newFilter);
        onSelectFilters(newFilter);
      } else {
        setBulkEmailCount(0);
        setBulkEmailList([]);

        const newFilter = {
          ...filter,
          emails: value
            .toLowerCase()
            .split(/[,;\r\n]+/)
            .map((email) => email.trim())
            .join('\n'),
        };

        if (!value) {
          delete newFilter.emails;
        }
        if (handleSelectAllUsers) {
          handleSelectAllUsers(false);
        }

        setFilter(newFilter);
        onSelectFilters(newFilter);
      }
    },
    [filter, onSelectFilters, bulkEmailCount]
  );

  const removeBulkEmails = useCallback(() => {
    setBulkEmailCount(0);
    setBulkEmailList([]);

    const newFilter = {
      ...filter,
      emails: '',
    };
    delete newFilter.emails;
    if (handleSelectAllUsers) {
      handleSelectAllUsers(false);
    }

    setFilter(newFilter);
    onSelectFilters(newFilter);
  }, [filter, onSelectFilters]);

  const handleCohortChange = useCallback(
    (name, value) => {
      const newFilter = { ...filter };

      if (value === 'All') {
        delete newFilter.cohort;
      } else {
        newFilter.cohort = value;
      }
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );
  const handleDateChange = useCallback(
    (dateType, value) => {
      const newFilter = {
        ...filter,
        date: { ...filter?.date, [dateType]: value },
      };
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleAlexAccessTypeChange = useCallback(
    (name, value) => {
      setAlexAccessType(value);
      const newFilter = { ...filter };

      if (value === 'All') {
        delete newFilter.alexAccessType;
      } else {
        newFilter.alexAccessType = value;
      }
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleAccessTypeChange = useCallback(
    (name, value) => {
      setAccessType(value);
      const newFilter = { ...filter };

      if (value === 'All') {
        delete newFilter.accessType;
      } else {
        newFilter.accessType = value;
      }
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const handleInvitedByChange = useCallback(
    (id, email) => {
      const newFilter = { ...filter };

      if (id) {
        newFilter.invited_by = { id, email };
      } else {
        delete newFilter.invited_by;
      }
      if (handleSelectAllUsers) {
        handleSelectAllUsers(false);
      }

      setFilter(newFilter);
      onSelectFilters(newFilter);
    },
    [filter, onSelectFilters]
  );

  const cohort = filter?.cohort || 'All';
  const startDate = formatDateForInput(filter?.date?.startDate);
  const endDate = formatDateForInput(filter?.date?.endDate);
  const userExpirationStartDate = formatDateForInput(
    filter?.date?.userExpirationStartDate
  );
  const userExpirationEndDate = formatDateForInput(
    filter?.date?.userExpirationEndDate
  );
  const alexExpirationStartDate = formatDateForInput(
    filter?.date?.alexExpirationStartDate
  );
  const alexExpirationEndDate = formatDateForInput(
    filter?.date?.alexExpirationEndDate
  );

  const cohortWithAll = [{ value: 'All', label: 'All' }, ...cohorts];

  const [alexAccessType, setAlexAccessType] = useState(
    selectedFilters?.alexAccessType || 'All'
  );

  const [accessType, setAccessType] = useState(
    selectedFilters?.accessType || 'All'
  );

  const alexAccessTypesWithAll = [
    { value: 'All', label: 'All' },
    ...alexAccessTypes,
  ];

  const hubspotAccessTypesWithAll = [
    { value: 'All', label: 'All' },
    ...hubspotAccessTypes,
  ];

  return (
    <div className="p-4 h-full overflow-y-auto">
      <h3 className="pb-4" style={{ fontSize: '30px', fontWeight: '800' }}>
        Filters
      </h3>

      {showFilters?.cohort && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-4">
            Cohort:
          </p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="cohort"
              value={cohort}
              options={cohortWithAll}
              onChange={handleCohortChange}
            />
          </div>
        </>
      )}
      {showFilters?.user_confirmation && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            User Confirmation:
          </p>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="Start Date"
              name="startDate"
              value={startDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('startDate', e.target.value)
              }
            />
            <div className="ml-4">
              <FormInput
                label="End Date"
                name="endDate"
                value={endDate}
                type="date"
                handleChange={(e) =>
                  handleDateChange('endDate', e.target.value)
                }
              />
            </div>
          </div>
        </>
      )}
      {showFilters?.user_expiration && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            User Expiration:
          </p>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="Start Date"
              name="userExpirationStartDate"
              value={userExpirationStartDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('userExpirationStartDate', e.target.value)
              }
            />
            <div className="ml-4">
              <FormInput
                label="End Date"
                name="userExpirationEndDate"
                value={userExpirationEndDate}
                type="date"
                handleChange={(e) =>
                  handleDateChange('userExpirationEndDate', e.target.value)
                }
              />
            </div>
          </div>
        </>
      )}
      {showFilters?.alex_expiration && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            Alex Expiration:
          </p>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="Start Date"
              name="alexExpirationStartDate"
              value={alexExpirationStartDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('alexExpirationStartDate', e.target.value)
              }
            />
            <div className="ml-4">
              <FormInput
                label="End Date"
                name="alexExpirationEndDate"
                value={alexExpirationEndDate}
                type="date"
                handleChange={(e) =>
                  handleDateChange('alexExpirationEndDate', e.target.value)
                }
              />
            </div>
          </div>
        </>
      )}
      {showFilters?.alex_access_type && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            Alex Access Type:
          </p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="alexAccessType"
              value={alexAccessType}
              options={alexAccessTypesWithAll}
              onChange={handleAlexAccessTypeChange}
            />
          </div>
        </>
      )}
      {showFilters?.user_access_type && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            User Access Type:
          </p>
          <div className="flex mt-2 mb-4">
            <SelectInput
              name="accessType"
              value={accessType}
              options={hubspotAccessTypesWithAll}
              onChange={handleAccessTypeChange}
            />
          </div>
        </>
      )}

      {showFilters?.invited_by && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            Sent by:
          </p>
          <div className="flex mt-2 mb-4">
            <CraInternalContactSelect
              className="w-full"
              hideLabel
              value={filter?.invited_by?.id}
              selectedEmail={filter?.invited_by?.email}
              onChange={handleInvitedByChange}
              selectedSummaryLabel="Sent by: "
              emptySummaryText="No sender selected — type at least two characters above to search"
            />
          </div>
        </>
      )}

      {showFilters?.date && (
        <>
          <p className="text-sm uppercase text-charcoal font-bold mt-6">
            Filter Date:
          </p>
          <div className="flex mt-2 mb-4">
            <FormInput
              label="Start Date"
              name="startDate"
              value={startDate}
              type="date"
              handleChange={(e) =>
                handleDateChange('startDate', e.target.value)
              }
            />
            <div className="ml-4">
              <FormInput
                label="End Date"
                name="endDate"
                value={endDate}
                type="date"
                handleChange={(e) =>
                  handleDateChange('endDate', e.target.value)
                }
              />
            </div>
          </div>
        </>
      )}

      {showFilters?.email && (
        <>
          <div className="flex flex-col mt-6 pb-4" style={{ width: '340px' }}>
            <label
              htmlFor="email-textarea"
              className="text-sm text-charcoal font-bold pb-2"
            >
              Email (you can separate them by comma, semicolon or line break):
            </label>

            {bulkEmailCount > 0 && (
              <div className="mb-2 p-3 border-2 border-gray rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-charcoal">
                    {bulkEmailCount} emails loaded from CSV
                  </span>
                  <button
                    type="button"
                    className="flex items-center justify-center p-1 hover:bg-gray-200 rounded"
                    onClick={removeBulkEmails}
                    title="Remove bulk emails"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        stroke="#6357B5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M18 6 6 18M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {bulkEmailCount === 0 && (
              <>
                <textarea
                  id="email-textarea"
                  name="content"
                  value={bulkEmailCount > 0 ? '' : filter?.emails || ''}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  rows={8}
                  className="border-2 rounded p-4 flex-grow"
                  placeholder={
                    bulkEmailCount > 0
                      ? 'Bulk emails loaded. Clear above to edit manually.'
                      : ''
                  }
                  disabled={bulkEmailCount > 0}
                />
                <button
                  type="button"
                  className="p-4  font-semibold bg-purple-100 hover:bg-purple-200 rounded-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    emailFileRef.current.click();
                  }}
                >
                  Upload emails
                </button>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleEmailFileUpload}
                  ref={emailFileRef}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>
        </>
      )}
      <div className="flex-col pb-4">
        <button
          type="button"
          className="underline cursor-pointer bg-transparent border-none p-0 appearance-none"
          onClick={() => {
            setBulkEmailCount(0);
            setBulkEmailList([]);
            setAlexAccessType('All');
            setAccessType('All');
            setFilter({});
            onSelectFilters({});
            if (handleSelectAllUsers) {
              handleSelectAllUsers(false);
            }
            if (onClearFilters) {
              onClearFilters();
            }
          }}
        >
          clear filters
        </button>
      </div>
    </div>
  );
};

export default Filters;
